/**
 * UX3: past runs for this agent (sidebar / history tab).
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiStudioRun } from '@/types/aiStudioAgent';

interface RunHistorySidebarProps {
  currentRunId: string;
  runs: AiStudioRun[];
  loading: boolean;
  onSelectRun: (runId: string) => void;
  onNewRun?: () => void;
}

export const RunHistorySidebar: React.FC<RunHistorySidebarProps> = ({
  currentRunId,
  runs,
  loading,
  onSelectRun,
  onNewRun,
}) => {
  const { t } = useLanguage();

  return (
    <div className="flex h-full flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.workspace.pastRuns')}
        </h2>
        {onNewRun && (
          <button
            type="button"
            onClick={onNewRun}
            className="text-base text-[var(--color-info)] hover:underline"
          >
            {t('aiStudio.workspace.newRun')}
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.workspace.loading')}
        </p>
      ) : runs.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.workspace.noPastRuns')}
        </p>
      ) : (
        <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
          {runs.map((run) => {
            const active = run.id === currentRunId;
            return (
              <li key={run.id}>
                <button
                  type="button"
                  onClick={() => onSelectRun(run.id)}
                  className={`w-full rounded px-2 py-2 text-left text-base ${
                    active
                      ? 'bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]'
                      : 'text-[var(--color-surface-text-secondary)] hover:bg-[var(--color-secondary-bg)]/60'
                  }`}
                >
                  <span className="block truncate font-medium text-[var(--color-surface-text)]">
                    {run.runName}
                  </span>
                  <span className="block text-sm text-[var(--color-surface-text-secondary)]">
                    {new Date(run.updatedAt).toLocaleString()}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
